import { mapValues } from "lodash";
import { useEffect, useRef, useState } from "react";
import { customActionTrackerInDD } from "../../../../../../utils";
import {
  FILE_EXT_CSV,
  ITEMS_PER_FILE_BY_FILE_TYPE,
  REJECT_REASON_API,
  REJECT_REASON_UNMOUNT,
} from "../constants";
import { flatten } from "../utils";
import fetchDataForExport from "./fetchDataForExport";

const INITIAL_PROGRESS = 1;

export const handleError = (error, exportStats, onError) => {
  if (error === REJECT_REASON_UNMOUNT) {
    return;
  }

  if (onError) {
    onError(error, exportStats);
    return;
  }

  const failReason =
    error === REJECT_REASON_API
      ? "Failed to download all records due to a network error."
      : "Something went wrong.";

  alert(
    `${failReason}\nPlease try again after some time or contact support if the issue persists.`
  );
};

function useDataExport({
  buttonConfigs,
  fetchFn,
  fileName,
  filterParams,
  itemsPerPage,
  totalItems: totalItemsProp,
  transforms,
  onError,
  onTruncate,
}) {
  const [isExporting, setIsExporting] = useState(false);
  const [progressPercent, setProgressPercent] = useState(INITIAL_PROGRESS);
  const abortController = useRef(new AbortController());
  const totalItemsRef = useRef(totalItemsProp);

  useEffect(() => {
    if (typeof totalItemsProp === "number" && !isNaN(totalItemsProp)) {
      totalItemsRef.current = totalItemsProp;
    }
  }, [totalItemsProp]);

  useEffect(() => {
    return () => {
      abortController.current?.abort?.(REJECT_REASON_UNMOUNT);
    };
  }, []);

  const startExport = async (exportButtonText) => {
    const startTime = performance.now();
    const fileType = buttonConfigs[exportButtonText] || FILE_EXT_CSV;
    abortController.current = new AbortController();

    const totalItems = totalItemsRef.current;
    const isEmptyFile = totalItems === 0;
    const itemsPerFile = isEmptyFile
      ? 1
      : ITEMS_PER_FILE_BY_FILE_TYPE[fileType] || totalItems;

    const exportStats = {
      data: [],
      headers: {},
      truncatedFields: [],
      fileName,
      fileType,
      totalItems,
      itemsPerFile,
      totalFilesToDownload: isEmptyFile
        ? 1
        : Math.ceil(totalItems / itemsPerFile),
      itemsPerPage,
      totalPagesToFetch: isEmptyFile ? 1 : Math.ceil(totalItems / itemsPerPage),
      countOfAPIFailures: 0,
      countOfFilesDownloaded: 0,
      currentPageNumber: 0,
      cursor: "",
    };

    if (!transforms.flatModel) {
      transforms.flatModel = mapValues(
        flatten(transforms?.model, true),
        () => ""
      );
    }

    setIsExporting(true);
    setProgressPercent(INITIAL_PROGRESS);

    try {
      await fetchDataForExport({
        abortController: abortController.current,
        exportStats,
        fetchFn,
        filterParams,
        onProgress: setProgressPercent,
        onTruncate,
        transforms,
      });
    } catch (error) {
      console.error(error);
      handleError(error, exportStats, onError);
    } finally {
      setIsExporting(false);
      setProgressPercent(INITIAL_PROGRESS);

      const endTime = performance.now();
      customActionTrackerInDD(
        startTime,
        endTime,
        `click on ${exportButtonText}`
      );
    }
  };

  return {
    isExporting,
    progressPercent,
    startExport,
  };
}

export default useDataExport;
